import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { clamp, easeOutCubic, prefersReducedMotion } from '@/lib/motion'

interface CountUpProps {
    value: number
    duration?: number
}

const format = (value: number): string => value.toLocaleString('en-US')

/** Renders the final number on the server, then counts up the first time it scrolls into view. */
export const CountUp: FC<CountUpProps> = ({ value, duration = 1400 }): ReactNode => {
    const ref = useRef<HTMLSpanElement>(null)
    const [shown, setShown] = useState(value)

    useEffect(() => {
        const element = ref.current
        if (!element || prefersReducedMotion() || !('IntersectionObserver' in window)) return
        if (element.getBoundingClientRect().top < window.innerHeight) return

        let frame = 0
        setShown(0)

        const observer = new IntersectionObserver(([entry]) => {
            if (!entry?.isIntersecting) return
            observer.disconnect()

            const start = performance.now()

            const step = (now: number): void => {
                const progress = clamp((now - start) / duration)
                setShown(Math.round(value * easeOutCubic(progress)))
                if (progress < 1) frame = requestAnimationFrame(step)
            }

            frame = requestAnimationFrame(step)
        }, { threshold: 0.5 })

        observer.observe(element)

        return () => {
            observer.disconnect()
            cancelAnimationFrame(frame)
            setShown(value)
        }
    }, [value, duration])

    return (
        <span ref={ref} className='tabular-nums'>
            {format(shown)}
        </span>
    )
}
